/**
 * What the account is allowed and what it is using, as one answer.
 *
 * Two services hold the halves: the stripe service says what the plan entitles, the casting
 * backend meters what has been used. Every surface that shows usage needs both, so they are
 * fetched together here and cached briefly - the header chip and the account page must never
 * disagree, and switching between them should not re-ask.
 */
import { myEntitlements } from './billing';
import { myUsage } from './usage';

const TTL_MS = 60000;
const GB = 1024 * 1024 * 1024;

let pending = null;
let cachedAt = 0;

/** Bytes as people read them: MB below a gigabyte, GB above, TB at the top. */
export function formatBytes(n) {
  const bytes = Number(n) || 0;
  if (bytes >= 1024 * GB) return `${(bytes / (1024 * GB)).toFixed(1)} TB`;
  if (bytes >= GB)
    return `${(bytes / GB).toFixed(bytes >= 10 * GB ? 0 : 1)} GB`;
  if (bytes >= 1024 * 1024) return `${Math.round(bytes / (1024 * 1024))} MB`;
  return `${Math.round(bytes / 1024)} KB`;
}

export function formatHours(seconds) {
  const h = (Number(seconds) || 0) / 3600;
  if (h === 0) return '0 h';
  if (h >= 10 || Number.isInteger(h)) return `${Math.round(h)} h`;
  return `${h.toFixed(1)} h`;
}

export function formatDate(value) {
  try {
    return new Date(value).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
    });
  } catch (e) {
    return '';
  }
}

/**
 * One meter: what is used, what is allowed, and how close that is.
 * Amber from 80%, red at the limit, which is the warning arriving before any block.
 */
function meter(used, allowed, format) {
  const u = Number(used) || 0;
  const a = Number(allowed) || 0;
  const pct = a > 0 ? Math.min(100, Math.round((u / a) * 100)) : 0;
  return {
    used: u,
    allowed: a,
    pct,
    usedLabel: format(u),
    allowedLabel: format(a),
    tone: pct >= 100 ? 'over' : pct >= 80 ? 'warn' : 'ok',
  };
}

function shape(ent, usage) {
  const entLimits = (ent && ent.limits) || {};
  const enforcement = (usage && usage.enforcement) || {};

  // Both services answer with limits, and for a few seconds after a plan changes they can
  // disagree, because the meter caches what billing told it. Billing is the authority on
  // what an account is entitled to, so when the two name different tiers its answer wins.
  // Anything else shows a plan name from one source beside numbers from the other, which
  // is worse than either being briefly wrong.
  const agree = !usage || !usage.tier || !ent || usage.tier === ent.tier;
  const limits = agree && usage && usage.limits ? usage.limits : entLimits;
  return {
    tier: (ent && ent.tier) || 'secure',
    planName: ((ent && ent.planName) || 'Secure').replace(/^Decast /, ''),
    status: (ent && ent.status) || 'none',
    paying: !!ent && ent.status !== 'none',
    periodStart: ent && ent.periodStart,
    periodEnd: ent && ent.periodEnd,
    limits,
    storage: meter(
      usage && usage.storage_bytes,
      limits.storageBytes,
      formatBytes
    ),
    ai: meter(
      usage && usage.ai_seconds_used,
      limits.aiSecondsPerMonth,
      formatHours
    ),
    credentials: limits.credentials || 0,
    dids: limits.dids || 0,
    // what the server would do right now, so the app never disagrees with it
    recordingBlocked: !!enforcement.recording_blocked,
    aiBlocked: !!enforcement.ai_blocked,
    enforcementMode: enforcement.mode || 'off',
    // a dated reprieve for an account that was already over a limit when it appeared
    grace: (usage && usage.grace) || null,
    // the meter is newer than the plans; say so rather than showing a zero that means nothing
    metered: !!usage,
  };
}

/**
 * The last answer, shared by every surface that shows usage.
 *
 * The header chip and the plan page must never disagree, and a number that changed because
 * someone deleted a recording should change everywhere at once. So the answer lives here and
 * components subscribe to it rather than each fetching their own.
 */
const subscribers = new Set();
let summary = null;

function publish(next) {
  summary = next;
  subscribers.forEach((fn) => {
    try {
      fn(summary);
    } catch (e) {
      // a broken subscriber must not stop the others hearing about it
    }
  });
}

/**
 * Listen for the account summary. Called immediately with the current answer when there is
 * one. Returns the function that stops listening, which components call on destroy.
 */
export function subscribeAccount(fn) {
  subscribers.add(fn);
  if (summary) fn(summary);
  return () => subscribers.delete(fn);
}

/** The current answer without asking for one, or null before the first fetch. */
export function currentAccount() {
  return summary;
}

/**
 * Entitlements and usage, merged. Rejects only when entitlements cannot be read: a missing
 * meter still leaves a plan worth showing.
 */
export function accountSummary(force = false) {
  if (!force && pending && Date.now() - cachedAt < TTL_MS) return pending;
  cachedAt = Date.now();
  pending = myEntitlements()
    .then((ent) =>
      // a forced refresh means something changed, so skip the server's cache too
      myUsage({ fresh: force })
        .catch(() => null)
        .then((usage) => {
          const next = shape(ent, usage);
          publish(next);
          return next;
        })
    )
    .catch((e) => {
      pending = null;
      throw e;
    });
  return pending;
}

/**
 * Ask again now, because something changed the numbers: a recording deleted, a cast created,
 * a plan bought. Never rejects, so a caller can fire it and forget.
 */
export function refreshAccount() {
  return accountSummary(true).catch(() => summary);
}

/** After a checkout or a plan change, the cached answer is stale. */
export function clearAccountCache() {
  pending = null;
  cachedAt = 0;
}

// Coming back to a tab that has been sitting for a while: ask again rather than showing
// whatever was true when it was left.
const STALE_AFTER_MS = 5 * 60 * 1000;
if (typeof document !== 'undefined' && document.addEventListener) {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    if (!summary || Date.now() - cachedAt < STALE_AFTER_MS) return;
    refreshAccount();
  });
}

/** On sign-out: the next person to use this browser must not see the last one's numbers. */
export function resetAccount() {
  clearAccountCache();
  publish(null);
}
